import { supabase } from "@/integrations/supabase/client";
import { registerPushToken } from "@/lib/push.functions";
import { fetchGeo } from "@/lib/pushInterest";

const appId = import.meta.env.VITE_LOVABLE_CONNECTOR_FIREBASE_MESSAGING_APP_ID as string | undefined;
const vapidKey = import.meta.env.VITE_LOVABLE_CONNECTOR_FIREBASE_MESSAGING_VAPID_KEY as string | undefined;
const firebaseConfig = {
  apiKey: (import.meta.env.VITE_LOVABLE_CONNECTOR_FIREBASE_MESSAGING_WEB_API_KEY as string) ?? "",
  projectId: (import.meta.env.VITE_LOVABLE_CONNECTOR_FIREBASE_MESSAGING_PROJECT_ID as string) ?? "",
  appId: appId ?? "",
  messagingSenderId: appId?.split(":")[1] ?? "",
};

export const PUSH_TOKEN_KEY = "sdb_push_token";

type FirebasePushModules = {
  initializeApp: typeof import("firebase/app")["initializeApp"];
  getApps: typeof import("firebase/app")["getApps"];
  getMessaging: typeof import("firebase/messaging")["getMessaging"];
  getToken: typeof import("firebase/messaging")["getToken"];
  isSupported: typeof import("firebase/messaging")["isSupported"];
};

let modulesPromise: Promise<FirebasePushModules> | null = null;
let registrationPromise: Promise<ServiceWorkerRegistration> | null = null;
let enablePromise: Promise<PushResult> | null = null;

export type PushResult =
  | { status: "registered"; token: string }
  | { status: "error"; detail: string }
  | { status: "not-configured" | "unsupported" | "open-in-new-tab" | "denied" | "ios-install" };

export function isIOS() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}
export function isStandalone() {
  return window.matchMedia?.("(display-mode: standalone)").matches || (navigator as any).standalone === true;
}

export function getStoredPushToken() {
  try {
    return localStorage.getItem(PUSH_TOKEN_KEY);
  } catch {
    return null;
  }
}

/** Espera o service worker ficar realmente ativo: o navegador rejeita a assinatura enquanto ele instala. */
async function waitForActive(reg: ServiceWorkerRegistration): Promise<ServiceWorkerRegistration> {
  if (reg.active) return reg;
  const pending = reg.installing ?? reg.waiting;
  if (pending) {
    await new Promise<void>((resolve) => {
      const done = () => {
        if (pending.state === "activated" || pending.state === "redundant") {
          pending.removeEventListener("statechange", done);
          resolve();
        }
      };
      pending.addEventListener("statechange", done);
      done();
    });
  }
  await navigator.serviceWorker.ready;
  return reg;
}

function loadPushModules() {
  modulesPromise ??= Promise.all([import("firebase/app"), import("firebase/messaging")]).then(([app, messaging]) => ({
    initializeApp: app.initializeApp,
    getApps: app.getApps,
    getMessaging: messaging.getMessaging,
    getToken: messaging.getToken,
    isSupported: messaging.isSupported,
  }));
  return modulesPromise;
}

function prepareServiceWorker() {
  if (!("serviceWorker" in navigator)) return Promise.resolve(null);
  registrationPromise ??= (() => {
    const query = new URLSearchParams(firebaseConfig).toString();
    return navigator.serviceWorker
      .register(`/firebase-messaging-sw.js?${query}`)
      .then(waitForActive)
      .catch((error) => {
        registrationPromise = null;
        throw error;
      });
  })();
  return registrationPromise;
}

/** Prepara os recursos lentos sem pedir permissão ao usuário. */
export async function preparePush() {
  if (!firebaseConfig.apiKey || !firebaseConfig.projectId || !appId || !vapidKey || !firebaseConfig.messagingSenderId) return;
  if (!("Notification" in window) || !("serviceWorker" in navigator) || window.top !== window.self) return;
  await Promise.all([loadPushModules(), prepareServiceWorker()]);
}

async function obtainToken(): Promise<string | null> {
  const [{ initializeApp, getApps, getMessaging, getToken }, reg] = await Promise.all([
    loadPushModules(),
    prepareServiceWorker(),
  ]);
  if (!reg) return null;
  const app = getApps()[0] ?? initializeApp(firebaseConfig);
  return getToken(getMessaging(app), { vapidKey, serviceWorkerRegistration: reg });
}


function describeDevice() {
  const ua = navigator.userAgent;
  const os = isIOS() ? "iPhone" : /android/i.test(ua) ? "Android" : /windows/i.test(ua) ? "Windows" : /mac os/i.test(ua) ? "Mac" : /linux/i.test(ua) ? "Linux" : "Outro";
  const br = /edg\//i.test(ua) ? "Edge" : /opr\//i.test(ua) ? "Opera" : /firefox/i.test(ua) ? "Firefox" : /chrome/i.test(ua) ? "Chrome" : /safari/i.test(ua) ? "Safari" : "Navegador";
  return `${os} · ${br}`;
}

async function saveToken(token: string, sessionPromise = supabase.auth.getSession()) {
  const [{ data }, geo] = await Promise.all([sessionPromise, fetchGeo()]);
  const previousToken = getStoredPushToken();
  await registerPushToken({
    data: {
      token,
      previousToken,
      platform: describeDevice(),
      accessToken: data.session?.access_token ?? null,
      geo,
    },
  });
  localStorage.setItem(PUSH_TOKEN_KEY, token);
  window.dispatchEvent(new CustomEvent("push-registration-change"));
}

type ForegroundPayload = {
  data?: Record<string, string>;
  notification?: { title?: string; body?: string; image?: string };
};

/** Exibe pushes recebidos enquanto a página está aberta e em foco. */
export async function listenForForegroundPush(): Promise<() => void> {
  if (!("Notification" in window) || Notification.permission !== "granted") return () => undefined;
  if (!("serviceWorker" in navigator) || !appId || !firebaseConfig.apiKey) return () => undefined;
  const [{ initializeApp, getApps }, { getMessaging, isSupported, onMessage }] = await Promise.all([
    import("firebase/app"),
    import("firebase/messaging"),
  ]);
  if (!(await isSupported())) return () => undefined;
  const app = getApps()[0] ?? initializeApp(firebaseConfig);
  return onMessage(getMessaging(app), async (payload: ForegroundPayload) => {
    if (document.visibilityState !== "visible") return;
    const title = payload.data?.title || payload.notification?.title || "Shopping do Bem";
    const body = payload.data?.body || payload.notification?.body || "Você recebeu uma nova oportunidade.";
    const link = payload.data?.link || "/";
    const image = payload.data?.image || payload.notification?.image;
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.showNotification(title, {
        body,
        icon: payload.data?.icon || "/favicon.png",
        ...(image ? { image } : {}),
        data: {
          link,
          cid: payload.data?.cid ?? "",
          sid: payload.data?.sid ?? "",
          sig: payload.data?.sig ?? "",
          actions: payload.data?.actions ?? "[]",
        },
        ...(() => {
          try {
            const acts = JSON.parse(payload.data?.actions || "[]") as { title: string }[];
            return acts.length ? { actions: acts.slice(0, 2).map((a, i) => ({ action: `a${i}`, title: a.title })) } : {};
          } catch {
            return {};
          }
        })(),
        tag: `shopping-do-bem-${Date.now()}`,
      });
    } catch (error) {
      console.error("foreground push display failed", error);
    }
  });
}

/** Call from a click handler. */
export async function enablePush(): Promise<PushResult> {
  if (!firebaseConfig.apiKey || !firebaseConfig.projectId || !appId || !vapidKey || !firebaseConfig.messagingSenderId)
    return { status: "not-configured" };
  if (isIOS() && !isStandalone()) return { status: "ios-install" };
  if (!("Notification" in window) || !("serviceWorker" in navigator)) return { status: "unsupported" };
  const { isSupported } = await loadPushModules();
  if (!(await isSupported())) return { status: "unsupported" };
  if (window.top !== window.self) return { status: "open-in-new-tab" };
  if (enablePromise) return enablePromise;
  enablePromise = (async () => {
    try {
      const sessionPromise = supabase.auth.getSession();
      const permission = Notification.permission === "granted" ? "granted" : await Notification.requestPermission();
      if (permission !== "granted") return { status: "denied" } as PushResult;
      const token = await obtainToken();
      if (!token) return { status: "error", detail: "O Firebase não retornou um token." } as PushResult;
      await saveToken(token, sessionPromise);
      return { status: "registered", token } as PushResult;
    } catch (e: unknown) {
      console.error("push enable failed", e);
      const detail = e instanceof Error ? e.message : String(e);
      return { status: "error", detail } as PushResult;
    } finally {
      enablePromise = null;
    }
  })();
  return enablePromise;
}

/** Silently refreshes the token when permission was already granted (links login too). */
export async function refreshPushIfGranted() {
  try {
    if (!("Notification" in window) || Notification.permission !== "granted") return;
    if (!vapidKey || !firebaseConfig.apiKey) return;
    if (window.top !== window.self) return;
    const token = await obtainToken();
    if (token) await saveToken(token);
  } catch (e) {
    console.warn("push refresh", e);
  }
}

export const PUSH_MESSAGES: Record<Exclude<PushResult["status"], "registered" | "error">, string> = {
  "not-configured": "As notificações ainda não estão disponíveis.",
  unsupported: "Seu navegador não permite notificações.",
  "open-in-new-tab": "Abra o site em uma aba própria para ativar as notificações.",
  denied: "As notificações estão bloqueadas. Libere nas configurações do navegador para este site.",
  "ios-install": "No iPhone: toque em Compartilhar e em “Adicionar à Tela de Início”, abra por lá e ative.",
};

export function pushResultMessage(r: PushResult): string {
  if (r.status === "error") return `Falha ao ativar: ${r.detail}`;
  if (r.status === "registered") return "";
  return PUSH_MESSAGES[r.status];
}
